import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { Client } from '@ddv/models';
import { DropdownOption, isDropdownOption } from '@hs/ui-core-common';
import { FuzzyDate } from '@hs/ui-core-date';
import { InputSelectComponent, MultiSelectDropdownComponent } from '@hs/ui-core-dropdowns';
import { Fund, FundMultiSelectDropdownComponent } from '@hs/ui-core-fund';

import { LabeledRowComponent } from '../labeled-row/labeled-row.component';

@Component({
    selector: 'ddv-user-preferences-default-query-params',
    templateUrl: './default-query-params.component.html',
    styleUrls: ['./default-query-params.component.scss'],
    imports: [
        LabeledRowComponent,
        MultiSelectDropdownComponent,
        FundMultiSelectDropdownComponent,
        InputSelectComponent,
    ],
})
export class DefaultQueryParamsComponent implements OnChanges {
    @Input() fuzzyDates: FuzzyDate[] | undefined;
    @Input() fromDate: string | undefined;
    @Input() toDate: string | undefined;
    @Input() isMultiClient = false;
    @Input() clients: Client[] | undefined;
    @Input() clientCodes: string[] | undefined;
    @Input() funds: Fund[] = [];
    @Input() fundCodes: string[] | undefined;

    @Output() fromDateChange = new EventEmitter<string>();
    @Output() toDateChange = new EventEmitter<string>();
    @Output() clientCodesChange = new EventEmitter<string[]>();
    @Output() fundCodesChange = new EventEmitter<string[]>();

    protected errorMessage: string | undefined;
    protected fromDateOptions: DropdownOption[] = [];
    protected selectedFromDateOption: DropdownOption | undefined;
    protected fuzzyDateToOptions: DropdownOption[] = [];
    protected selectedToDateOption: DropdownOption | undefined;
    protected clientOptions: DropdownOption[] = [];
    protected selectedClientsOptions: DropdownOption[] = [];
    protected selectedFunds: Fund[] = [];

    ngOnChanges(): void {
        this.fromDateOptions = this.fuzzyDates?.map((date) => {
            return {
                text: date.name,
                value: date.name,
            };
        }) ?? [];
        this.selectedFromDateOption = this.fromDateOptions.find((option) => option.value === this.fromDate);

        this.fuzzyDateToOptions = this.fuzzyDates?.map((date) => {
            return {
                text: date.name,
                value: date.name,
            };
        }) ?? [];
        this.selectedToDateOption = this.fuzzyDateToOptions.find((option) => option.value === this.toDate);

        this.clientOptions = this.clients?.map((client) => {
            return {
                text: client.clientName,
                value: client.clientId,
            };
        }) ?? [];
        this.selectedClientsOptions = this.clientOptions.filter((option) => {
            return this.clientCodes?.includes(option.value);
        });

        this.selectedFunds = this.funds.filter((fund) => {
            return this.fundCodes?.includes(fund.code);
        });
    }

    protected onFromDateChange(fromDate: string | DropdownOption | undefined): void {
        if (fromDate === undefined || isDropdownOption(fromDate)) {
            this.selectedFromDateOption = fromDate;
            if (this.dateRangeIsValid()) {
                this.fromDateChange.emit(this.selectedFromDateOption?.value ?? '');
            }
        }
    }

    protected onToDateChange(toDate: string | DropdownOption | undefined): void {
        if (toDate === undefined || isDropdownOption(toDate)) {
            this.selectedToDateOption = toDate;
            if (this.dateRangeIsValid()) {
                this.toDateChange.emit(this.selectedToDateOption?.value ?? '');
            }
        }
    }

    protected onClientsChange(): void {
        const selectedClientCodes = this.selectedClientsOptions.map((option) => option.value);
        this.clientCodesChange.emit(selectedClientCodes);
    }

    protected onFundsChange(): void {
        const selectedFundCodes = this.selectedFunds.map((fund) => fund.code);
        this.fundCodesChange.emit(selectedFundCodes);
    }

    private dateRangeIsValid(): boolean {
        this.errorMessage = '';
        if (
            this.selectedFromDateOption?.value &&
            this.selectedToDateOption?.value &&
            (!this.isStartDateLesser(this.selectedFromDateOption?.value, this.selectedToDateOption?.value) &&
            this.selectedFromDateOption?.value !== this.selectedToDateOption?.value)
        ) {
            this.errorMessage = 'From date should not be greater than to date';
            return false;
        }

        return true;
    }

    private isStartDateLesser(startDateString: string, endDateString: string): boolean {
        const startDate = this.fuzzyDates?.find((fd) => fd.name === startDateString);
        const endDate = this.fuzzyDates?.find((fd) => fd.name === endDateString);

        return !!startDate?.commonDate.isBefore(endDate?.commonDate);
    }
}
